import React, { FC } from "react";
import Popover from "@mui/material/Popover";
import { t } from "i18next";
import FeatherIcon from "feather-icons-react";
import { Palette, UserRole } from "@green0meter/api-client";

import { StyledPopover } from "./SidebarAccountPopover.styled";

import { Box, Button, Stack, Typography } from "@components/base";
import { PLATFORM_LANGUAGES, PlatformLanguage } from "@constants";
import { useUserStore } from "@store/user";
import { useGetCurrentCompanyInfo } from "@api";

interface SidebarAccountPopoverProps {
  open: boolean;

  onClose(): void;

  anchorEl: React.RefObject<HTMLButtonElement>;
  languagePopoverOpen: boolean;
  setLanguagePopoverOpen: (open: boolean) => void;
  handleSettingsClick: () => void;
  handleLanguageChanged: (lng: PlatformLanguage) => Promise<void>;
  findLanguageName: (code: string) => string;
  handleLogout: () => void;
  handleAddFeaturesClick: () => void;
  currentLanguage: string;
}

const SidebarAccountPopover: FC<SidebarAccountPopoverProps> = ({
  open,
  onClose,
  anchorEl,
  languagePopoverOpen,
  setLanguagePopoverOpen,
  handleSettingsClick,
  handleLanguageChanged,
  findLanguageName,
  handleLogout,
  handleAddFeaturesClick,
  currentLanguage,
}) => {
  const { user } = useUserStore();
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);

  const isNotViewer = user?.role !== UserRole.Viewer;
  const isNotRespondent = user?.role !== UserRole.Respondent;
  const isNotAuditor = user?.role !== UserRole.Auditor;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl.current}
      onClose={onClose}
      anchorOrigin={{
        vertical: "center",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: -20,
      }}
    >
      <StyledPopover>
        <Stack spacing={1}>
          <Button
            startIcon={<FeatherIcon icon="settings" size={20} />}
            onClick={() => {
              handleSettingsClick();
              onClose();
            }}
            fullWidth
          >
            {t("sidebar.account.settings")}
          </Button>
          <Box>
            <Button
              startIcon={<FeatherIcon icon="globe" size={20} />}
              endIcon={<FeatherIcon icon="chevron-right" size={20} />}
              fullWidth
              onClick={() => setLanguagePopoverOpen(!languagePopoverOpen)}
            >
              <Stack justifyContent="space-between" width="100%" direction="row">
                <Typography color="secondary">
                  {findLanguageName(currentLanguage)}
                </Typography>
              </Stack>
            </Button>

            <Popover
              open={languagePopoverOpen}
              anchorEl={anchorEl.current}
              onClose={() => setLanguagePopoverOpen(false)}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: 105,
                horizontal: -290,
              }}
            >
              <StyledPopover sx={{ px: 1, minWidth: "auto" }}>
                <Stack
                  justifyContent="space-between"
                  width="100%"
                  direction="column"
                >
                  {PLATFORM_LANGUAGES.map((lng) => (
                    <Button
                      key={lng.code}
                      onClick={() => {
                        handleLanguageChanged(lng);
                        setLanguagePopoverOpen(false);
                        onClose();
                      }}
                    >
                      <Typography color="secondary">
                        {findLanguageName(lng.code)}
                      </Typography>
                    </Button>
                  ))}
                </Stack>
              </StyledPopover>
            </Popover>
          </Box>
          {isNotViewer && isNotRespondent && isNotAuditor && (
            <Button
              onClick={() => {
                handleAddFeaturesClick();
                onClose();
              }}
              startIcon={
                // eslint-disable-next-line react/jsx-no-literals
                <Box component="span" sx={{ fontSize: 20 }}>
                  &#128640;
                </Box>
              }
            >
              <Typography variant="body1" color="secondary">
                {t("sidebar.account.addFeatures")}
              </Typography>
            </Button>
          )}
          <Button
            startIcon={<FeatherIcon icon="log-out" size={20} />}
            color={
              !Object.values(Palette).includes(
                currentCompanyInfo?.appearance?.palette as Palette,
              )
                ? "secondary"
                : "error"
            }
            fullWidth
            onClick={() => {
              handleLogout();
              onClose();
            }}
            variant="text"
          >
            {t("sidebar.account.logOut")}
          </Button>
        </Stack>
      </StyledPopover>
    </Popover>
  );
};

export default SidebarAccountPopover;
